import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { getApiErrorMessage } from '@/lib/api';
import { 
  Buildings, 
  User, 
  Envelope, 
  LockKey, 
  Phone, 
  MapPin,
  Globe,
  IdentificationCard,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Hospital,
} from '@phosphor-icons/react';

const HOSPITAL_TYPES = [
  { value: 'general', label: 'General Hospital' },
  { value: 'specialty', label: 'Specialty Hospital' },
  { value: 'clinic', label: 'Clinic' },
  { value: 'diagnostic_center', label: 'Diagnostic Center' },
  { value: 'nursing_home', label: 'Nursing Home' },
  { value: 'multi_specialty', label: 'Multi-Specialty Hospital' },
  { value: 'research_institute', label: 'Research Institute' },
];

const DEPARTMENTS = [
  'General Medicine', 'Cardiology', 'Orthopedics', 'Pediatrics', 
  'Gynecology', 'Neurology', 'Dermatology', 'ENT', 'Ophthalmology',
  'Psychiatry', 'Oncology', 'Urology', 'Gastroenterology', 'Nephrology',
  'Pulmonology', 'Radiology', 'Pathology', 'Emergency Medicine', 'ICU'
];

const STEPS = [
  { id: 1, title: 'Hospital Info', icon: Buildings },
  { id: 2, title: 'Address', icon: MapPin },
  { id: 3, title: 'Admin Account', icon: User },
];

export default function HospitalRegistrationPage() {
  const navigate = useNavigate();
  const { registerHospital } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Hospital Info
    hospitalName: '',
    hospitalType: 'general',
    description: '',
    phone: '',
    alternatePhone: '',
    email: '',
    website: '',
    registrationNumber: '',
    licenseNumber: '',
    totalBeds: '',
    departments: [],
    
    // Address
    street: '',
    city: '',
    state: '',
    country: 'India',
    zipCode: '',
    landmark: '',
    
    // Admin Account
    adminName: '',
    adminEmail: '',
    adminPhone: '',
    password: '',
    confirmPassword: '',

    agreeTerms: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleDepartment = (dept) => {
    setFormData(prev => ({
      ...prev,
      departments: prev.departments.includes(dept)
        ? prev.departments.filter(d => d !== dept)
        : [...prev.departments, dept]
    }));
  };

  const validateStep = (step) => {
    switch(step) {
      case 1:
        if (!formData.hospitalName || !formData.phone || !formData.email) {
          toast.error('Please fill in all required fields');
          return false;
        }
        if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        return true;
      case 2:
        if (!formData.street || !formData.city || !formData.state || !formData.zipCode) {
          toast.error('Please fill in all address fields');
          return false;
        }
        return true;
      case 3:
        if (!formData.adminName || !formData.adminEmail || !formData.password) {
          toast.error('Please fill in all admin account fields');
          return false;
        }
        if (formData.password.length < 8) {
          toast.error('Password must be at least 8 characters');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          return false;
        }
        if (!formData.agreeTerms) {
          toast.error('Please agree to the terms and conditions');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;

    setIsLoading(true);
    try {
      const payload = {
        hospitalName: formData.hospitalName,
        hospitalType: formData.hospitalType,
        description: formData.description || undefined,
        phone: formData.phone,
        alternatePhone: formData.alternatePhone || undefined,
        email: formData.email,
        website: formData.website || undefined,
        registrationNumber: formData.registrationNumber || undefined,
        licenseNumber: formData.licenseNumber || undefined,
        totalBeds: formData.totalBeds ? Number(formData.totalBeds) : undefined,
        departments: formData.departments,
        street: formData.street,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        zipCode: formData.zipCode,
        landmark: formData.landmark || undefined,
        adminName: formData.adminName,
        adminEmail: formData.adminEmail,
        adminPhone: formData.adminPhone || undefined,
        password: formData.password,
        agreeTerms: formData.agreeTerms,
      };
      const result = await registerHospital(payload);
      if (result.success) {
        toast.success('Registration successful! Welcome to MediCare HMS.');
        navigate('/dashboard');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        const isActive = currentStep === step.id;
        const isCompleted = currentStep > step.id;
        
        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <motion.div
                initial={false}
                animate={{
                  scale: isActive ? 1.1 : 1,
                  backgroundColor: isCompleted ? 'var(--primary)' : isActive ? 'var(--primary)' : 'var(--muted)'
                }}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  isCompleted || isActive ? 'text-primary-foreground' : 'text-muted-foreground'
                }`}
              >
                {isCompleted ? (
                  <CheckCircle weight="fill" className="w-5 h-5" />
                ) : (
                  <Icon className="w-5 h-5" />
                )}
              </motion.div>
              <span className={`text-xs mt-1 hidden sm:block ${isActive ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                {step.title}
              </span>
            </div>
            {index < STEPS.length - 1 && (
              <div className={`w-12 sm:w-20 h-0.5 mx-1 ${currentStep > step.id ? 'bg-primary' : 'bg-muted'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderStep1 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="hospitalName">Hospital Name *</Label>
          <div className="relative">
            <Buildings className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="hospitalName"
              name="hospitalName"
              data-testid="hospital-name-input"
              placeholder="City General Hospital"
              value={formData.hospitalName}
              onChange={handleChange}
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="hospitalType">Hospital Type</Label>
          <Select value={formData.hospitalType} onValueChange={(v) => handleSelectChange('hospitalType', v)}>
            <SelectTrigger data-testid="hospital-type-select">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {HOSPITAL_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="totalBeds">Total Beds</Label>
          <Input
            id="totalBeds"
            name="totalBeds"
            data-testid="total-beds-input"
            type="number"
            placeholder="100"
            value={formData.totalBeds}
            onChange={handleChange}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number *</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="phone"
              name="phone"
              data-testid="hospital-phone-input"
              placeholder="+91 98765 43210"
              value={formData.phone}
              onChange={handleChange}
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="alternatePhone">Alternate Phone</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="alternatePhone"
              name="alternatePhone"
              data-testid="alternate-phone-input"
              placeholder="+91 98765 43211"
              value={formData.alternatePhone}
              onChange={handleChange}
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Hospital Email *</Label>
          <div className="relative">
            <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="email"
              name="email"
              data-testid="hospital-email-input"
              type="email"
              placeholder="info@hospital.com"
              value={formData.email}
              onChange={handleChange}
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="website">Website</Label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="website"
              name="website"
              data-testid="website-input"
              placeholder="https://www.hospital.com"
              value={formData.website}
              onChange={handleChange}
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="registrationNumber">Registration Number</Label>
          <div className="relative">
            <IdentificationCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="registrationNumber"
              name="registrationNumber"
              data-testid="registration-number-input"
              placeholder="REG-2024-XXXXX"
              value={formData.registrationNumber}
              onChange={handleChange}
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="licenseNumber">License Number</Label>
          <div className="relative">
            <IdentificationCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="licenseNumber"
              name="licenseNumber"
              data-testid="license-number-input"
              placeholder="LIC-XXXXX"
              value={formData.licenseNumber}
              onChange={handleChange}
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            name="description"
            data-testid="description-input"
            placeholder="Brief description of your hospital..."
            value={formData.description}
            onChange={handleChange}
            rows={3}
          />
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label>Departments</Label>
          <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-muted/30 max-h-32 overflow-y-auto">
            {DEPARTMENTS.map(dept => (
              <button
                key={dept}
                type="button"
                onClick={() => toggleDepartment(dept)}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  formData.departments.includes(dept)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                }`}
              >
                {dept}
              </button>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="street">Street Address *</Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="street"
              name="street"
              data-testid="street-input"
              placeholder="123 Healthcare Avenue, Near City Center"
              value={formData.street}
              onChange={handleChange}
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="city">City *</Label>
          <Input
            id="city"
            name="city"
            data-testid="city-input"
            placeholder="Mumbai"
            value={formData.city}
            onChange={handleChange}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="state">State *</Label>
          <Input
            id="state"
            name="state"
            data-testid="state-input"
            placeholder="Maharashtra"
            value={formData.state}
            onChange={handleChange}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="country">Country</Label>
          <Input
            id="country"
            name="country"
            data-testid="country-input"
            placeholder="India"
            value={formData.country}
            onChange={handleChange}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="zipCode">ZIP / PIN Code *</Label>
          <Input
            id="zipCode"
            name="zipCode"
            data-testid="zipcode-input"
            placeholder="400001"
            value={formData.zipCode}
            onChange={handleChange}
          />
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="landmark">Landmark</Label>
          <Input
            id="landmark"
            name="landmark"
            data-testid="landmark-input"
            placeholder="Opposite to City Mall"
            value={formData.landmark}
            onChange={handleChange}
          />
        </div>
      </div>
    </motion.div>
  );

  const renderStep3 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div className="p-4 rounded-lg bg-muted/50 border border-border mb-4">
        <p className="text-sm text-muted-foreground">
          Create an admin account to manage your hospital. This will be the primary administrator account with full access.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="adminName">Admin Full Name *</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="adminName"
              name="adminName"
              data-testid="admin-name-input"
              placeholder="Dr. John Smith"
              value={formData.adminName}
              onChange={handleChange}
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="adminEmail">Admin Email *</Label>
          <div className="relative">
            <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="adminEmail"
              name="adminEmail"
              data-testid="admin-email-input"
              type="email"
              placeholder="admin@hospital.com"
              value={formData.adminEmail}
              onChange={handleChange}
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="adminPhone">Admin Phone</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="adminPhone"
              name="adminPhone"
              data-testid="admin-phone-input"
              placeholder="+91 98765 43210"
              value={formData.adminPhone}
              onChange={handleChange}
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password *</Label>
          <div className="relative">
            <LockKey className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="password"
              name="password"
              data-testid="password-input"
              type="password"
              placeholder="Min. 8 characters"
              value={formData.password}
              onChange={handleChange}
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm Password *</Label>
          <div className="relative">
            <LockKey className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="confirmPassword"
              name="confirmPassword"
              data-testid="confirm-password-input"
              type="password"
              placeholder="Re-enter password"
              value={formData.confirmPassword}
              onChange={handleChange}
              className="pl-9"
            />
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 border border-border md:col-span-2">
          <Checkbox
            id="agreeTerms"
            checked={formData.agreeTerms}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, agreeTerms: checked }))}
            data-testid="agree-terms-checkbox"
          />
          <Label htmlFor="agreeTerms" className="text-sm leading-relaxed cursor-pointer">
            I agree to the <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link> and{' '}
            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
            I understand that my hospital data will be stored securely and processed according to applicable regulations.
          </Label>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-2/5 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/95 to-secondary/95" />
        </div>
        <div className="relative z-10 flex flex-col justify-between p-12 text-white h-full">
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="h-12 w-12 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                <Hospital className="w-7 h-7" weight="fill" />
              </div>
              <span className="text-2xl font-bold tracking-tight">MediCare HMS</span>
            </div>
          </div>
          
          <div className="max-w-md">
            <h1 className="text-4xl md:text-5xl font-light tracking-tight mb-6">
              Transform Your Hospital Management
            </h1>
            <p className="text-lg leading-relaxed opacity-90 mb-8">
              Join thousands of healthcare providers using MediCare to streamline operations, 
              improve patient care, and boost efficiency.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6" weight="fill" />
                <span>Easy setup in under 10 minutes</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6" weight="fill" />
                <span>Secure cloud-based records</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6" weight="fill" />
                <span>24/7 dedicated support</span>
              </div>
            </div>
          </div>

          <div className="text-sm opacity-70">
            © 2024 MediCare HMS. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-background overflow-y-auto">
        <Card className="w-full max-w-3xl border-border shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center gap-2 mb-4 lg:hidden">
              <div className="h-10 w-10 rounded-md bg-primary flex items-center justify-center">
                <Hospital className="w-5 h-5 text-primary-foreground" weight="fill" />
              </div>
              <span className="text-xl font-bold tracking-tight">MediCare HMS</span>
            </div>
            <CardTitle className="text-2xl font-semibold">Register Your Hospital</CardTitle>
            <CardDescription>Complete the form below to get started</CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            {renderStepIndicator()}
            
            <form onSubmit={handleSubmit}>
              <AnimatePresence mode="wait">
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && renderStep2()}
                {currentStep === 3 && renderStep3()}
              </AnimatePresence>

              <div className="flex items-center justify-between mt-8 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  data-testid="prev-step-button"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Previous
                </Button>

                {currentStep < 3 ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    data-testid="next-step-button"
                  >
                    Next
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={isLoading}
                    data-testid="submit-registration-button"
                    className="min-w-[150px]"
                  >
                    {isLoading ? 'Creating...' : 'Complete Registration'}
                  </Button>
                )}
              </div>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Already have an account? </span>
              <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
